import { cn } from '@@/lib/utils';
import { Label } from '@@/ui/primitives';
import { Input } from '@@/ui/primitives/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@@/ui/primitives/select';
import { Calendar, Clock } from 'lucide-react';

export enum ScheduleType {
  IMMEDIATELY = 'IMMEDIATELY',
  SCHEDULED = 'SCHEDULED'
}

export interface ScheduleData {
  scheduleType: ScheduleType;
  startDate: string;
  startTime: string;
  expirationDate: string;
  expirationTime: string;
  quietHoursDate: string;
  quietHoursTime: string;
  retryFailedCalls: string;
}

export const INITIAL_SCHEDULE_DATA: ScheduleData = {
  scheduleType: ScheduleType.IMMEDIATELY,
  startDate: '',
  startTime: '',
  expirationDate: '',
  expirationTime: '',
  quietHoursDate: '',
  quietHoursTime: '',
  retryFailedCalls: 'none'
};

export function ScheduleStep(props: { data: ScheduleData; onChange: (data: ScheduleData) => void }) {
  return (
    <div>
      <h2 className='text-xl font-semibold text-foreground mb-6'>Schedule Your Campaign</h2>

      <div className='space-y-6'>
        <ScheduleTypeSelector
          value={props.data.scheduleType}
          onChange={(scheduleType) => props.onChange({ ...props.data, scheduleType })}
        />

        {props.data.scheduleType === ScheduleType.SCHEDULED && (
          <FormSection
            title='Campaign Start Date and Time'
            description='Campaign will automatically stop at these values.
              Required for Compliance and resource management.'
          >
            <div className='grid grid-cols-2 gap-4'>
              <FormField label='Date'>
                <DateInput
                  value={props.data.startDate}
                  onChange={(startDate) => props.onChange({ ...props.data, startDate })}
                />
              </FormField>
              <FormField label='Time'>
                <TimeInput
                  value={props.data.startTime}
                  onChange={(startTime) => props.onChange({ ...props.data, startTime })}
                />
              </FormField>
            </div>
          </FormSection>
        )}

        <FormSection
          title='Campaign Expiration Date and Time'
          description='Campaign will automatically stop at these values.
            Required for Compliance and resource management.'
        >
          <div className='grid grid-cols-2 gap-4'>
            <FormField label='Date'>
              <DateInput
                value={props.data.expirationDate}
                onChange={(expirationDate) => props.onChange({ ...props.data, expirationDate })}
              />
            </FormField>
            <FormField label='Time'>
              <TimeInput
                value={props.data.expirationTime}
                onChange={(expirationTime) => props.onChange({ ...props.data, expirationTime })}
              />
            </FormField>
          </div>
        </FormSection>

        <FormSection title='Quiet Hours' description='No calls will happen during this time periods.'>
          <div className='grid grid-cols-2 gap-4'>
            <FormField label='Date'>
              <TimeInput
                value={props.data.quietHoursDate}
                onChange={(quietHoursDate) => props.onChange({ ...props.data, quietHoursDate })}
              />
            </FormField>
            <FormField label='Time'>
              <TimeInput
                value={props.data.quietHoursTime}
                onChange={(quietHoursTime) => props.onChange({ ...props.data, quietHoursTime })}
              />
            </FormField>
          </div>
        </FormSection>

        <FormField label='Retry Failed Calls'>
          <Select
            value={props.data.retryFailedCalls}
            onValueChange={(retryFailedCalls) => props.onChange({ ...props.data, retryFailedCalls })}
          >
            <SelectTrigger className='w-full'>
              <SelectValue placeholder='Select option' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='none'>No retries</SelectItem>
              <SelectItem value='1'>1 retry</SelectItem>
              <SelectItem value='2'>2 retries</SelectItem>
              <SelectItem value='3'>3 retries</SelectItem>
            </SelectContent>
          </Select>
        </FormField>

        <GuidelinesCard />
      </div>
    </div>
  );
}

function ScheduleTypeSelector(props: { value: ScheduleType; onChange: (value: ScheduleType) => void }) {
  return (
    <div className='flex gap-4'>
      <ScheduleTypeButton
        label='Start Immediately'
        isSelected={props.value === ScheduleType.IMMEDIATELY}
        onClick={() => props.onChange(ScheduleType.IMMEDIATELY)}
      />
      <ScheduleTypeButton
        label='Schedule Campaign'
        isSelected={props.value === ScheduleType.SCHEDULED}
        onClick={() => props.onChange(ScheduleType.SCHEDULED)}
      />
    </div>
  );
}

function ScheduleTypeButton(props: { label: string; isSelected: boolean; onClick: () => void }) {
  return (
    <button
      type='button'
      onClick={props.onClick}
      className={cn(
        'flex items-center gap-3 px-4 py-4 rounded-lg border-2',
        'transition-all text-sm font-medium',
        props.isSelected ? 'border-blue bg-card' : 'border-border bg-card hover:border-blue-800'
      )}
    >
      <div
        className={cn(
          'w-5 h-5 rounded-full border-2 flex items-center justify-center',
          props.isSelected ? 'border-blue' : 'border-border'
        )}
      >
        {props.isSelected && <div className='w-2.5 h-2.5 rounded-full bg-blue' />}
      </div>
      {props.label}
    </button>
  );
}

function FormSection(props: { title: string; description: string; children: React.ReactNode }) {
  return (
    <div className='space-y-3'>
      <div>
        <h3 className='text-sm font-medium text-foreground'>{props.title}</h3>
        <p className='text-xs text-muted-foreground mt-1'>{props.description}</p>
      </div>
      {props.children}
    </div>
  );
}

function FormField(props: { label: string; children: React.ReactNode }) {
  return (
    <div className='space-y-2'>
      <Label className='text-sm font-medium text-foreground'>{props.label}</Label>
      {props.children}
    </div>
  );
}

function DateInput(props: { value: string; onChange: (value: string) => void }) {
  return (
    <div className='relative'>
      <Input
        type='text'
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        placeholder='mm/dd/yyyy'
        className='pr-10'
      />
      <Calendar
        className='absolute right-3 top-1/2 -translate-y-1/2
          w-4 h-4 text-muted-foreground pointer-events-none'
      />
    </div>
  );
}

function TimeInput(props: { value: string; onChange: (value: string) => void }) {
  return (
    <div className='relative'>
      <Input
        type='text'
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        placeholder='hh:mm'
        className='pr-10'
      />
      <Clock
        className='absolute right-3 top-1/2 -translate-y-1/2
          w-4 h-4 text-muted-foreground pointer-events-none'
      />
    </div>
  );
}

function GuidelinesCard() {
  return (
    <div className='bg-blue-500 rounded-lg p-5'>
      <h4 className='text-sm font-semibold text-foreground mb-3'>Campaign Duration Guidelines</h4>
      <ul className='space-y-1.5 text-sm text-foreground'>
        <li className='flex items-start gap-2'>
          <span className='text-muted-foreground'>•</span>
          Emergency alerts: Run for up to 24 hours
        </li>
        <li className='flex items-start gap-2'>
          <span className='text-muted-foreground'>•</span>
          Service notifications: Maximum 7 days
        </li>
        <li className='flex items-start gap-2'>
          <span className='text-muted-foreground'>•</span>
          Surveys: Can stay active for 30 days
        </li>
        <li className='flex items-start gap-2'>
          <span className='text-muted-foreground'>•</span>
          General communications: Limit to 14 days
        </li>
      </ul>
    </div>
  );
}
